import useAppStore from '$appStore'
import { DayOfWeek, RangeFilter } from '$constants'
import { CompanyPreferences, getActiveCompanySettings } from '$utils'
import dayjs, { Dayjs, OpUnitType } from 'dayjs'

/**
 * Formats time in minute to the expected display value
 * @param minutes the amount of minutes to be formatted
 * @returns formatted time string
 */
export function formatDatetimeInterval(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  const interval: string[] = [] // Explicitly specify the type as an array of strings

  if (hours > 0) {
    interval.push(hours.toString() + ' hrs')
  }

  if (mins > 0) {
    interval.push(mins.toString() + ' mins')
  } else {
    interval.push(0 + ' mins')
  }

  return interval.join(' ')
}

/**
 * Formats time in hours to the expected display value
 * @param hours the amount of hours to be formatted
 * @returns formatted time string
 */
export function formatHoursToInterval(hours: number): string {
  // check if hours have decimal, thus needs minutes value
  if (hours === 0) return '0m'

  if (hours % 1 == 0) {
    return hours + 'h 0m'
  }

  const truncHours = Math.trunc(hours) // get the integer value of hours
  // if there is no integer value then time has only minutes
  if (truncHours == 0) {
    return (hours * 60).toFixed(0) + 'm'
  }

  const decMins = hours % 1 // get the decimal value of mins
  const mins = (decMins * 60).toFixed(0) // convert decimal into integer minutes then round to closest minute

  return truncHours + 'h ' + mins + 'm'
}

/**
 * Formats time in hours to its decimal value upto 2 dp
 * @param hours the hours to be evaluated
 * @returns formatted time string
 */
export function formatHoursToDecimal(hours: number): string {
  return hours.toFixed(2)
}

/**
 * Function to preserve the timezone preference of the company
 * @param date to convert
 * (if date param is a dayjs object, it goes to the dayjs constructor anyways)
 * @returns
 */
export function setCompanyTimeZone(date: string | Dayjs) {
  const { timeZonePreference } = getActiveCompanySettings()

  return dayjs(date).tz(timeZonePreference)
}

/**
 * Function to format a dayjs object in the timezone preference of the company
 * @param date to format
 * @returns
 */
export function formatInCompanyTimeZone(date: string | Dayjs, format: string) {
  return setCompanyTimeZone(date).format(format)
}

/**
 * Get closest day in the future with start time startTime
 * @param date date to take as reference
 * @param dayIndex closest day index
 * @param startTime start time for the closest day (format e.g. "07:00:00")
 * @returns
 */
export const getClosestDayWithStartTime = (
  date: Dayjs,
  dayIndex: number,
  startTime: string
): dayjs.Dayjs => {
  const inputDate = dayjs(date)
  const currentDayOfWeek = inputDate.day() // Get the day of the week (0-6, where 0 is Sunday)

  // Calculate the offset to the desired day of the week
  const offsetToDesiredDay = (dayIndex - currentDayOfWeek + 7) % 7

  // Add the offset to get the closest desired day of the week
  let closestDayOfWeek = inputDate.add(offsetToDesiredDay, 'day').startOf('day')

  // Parse the startTime string and set it as the start time of the day
  const [hours, minutes, seconds] = startTime.split(':').map(Number)
  closestDayOfWeek = closestDayOfWeek
    .set('hour', hours)
    .set('minute', minutes)
    .set('second', seconds)

  return closestDayOfWeek
}

/**
 * Given a date, it will retrieve the same date in 'YYYY-MM-DD' format without parsing the timezone (usefull for when the API expects DateOnly)
 * @param date
 * @returns
 */
export const getDateStringWithoutTimeZone = (date: Dayjs): string => {
  const newDate = date.toDate()

  const month = date.get('month') + 1
  return `${date.get('year')}-${month < 10 ? `0${month}` : month}-${
    newDate.getDate() < 10 ? '0' + newDate.getDate() : newDate.getDate()
  }`
}

/**
 * Function to set the company start time to any date
 * @param date
 * @returns the new date with the company start time and timezone set.
 * Usage: returnDate.format() and you will get the date with the company start time and timezone set
 */

export const setCompanyDayStartAndTimeZone = (
  date: Dayjs | string,
  updateTimezone = true
) => {
  date = typeof date === 'string' ? dayjs(date) : date
  const { dayStartPreference, timeZonePreference } = getActiveCompanySettings()

  // Parse the given time string
  const [hours, minutes] = dayStartPreference.split(':').map(Number)

  // Convert the date to the specified timezone
  const dateWithTimezone = updateTimezone ? date.tz(timeZonePreference) : date

  // Set the hours and minutes to the given date
  const dateWithTime = dateWithTimezone
    .hour(hours)
    .minute(minutes)
    .second(0)
    .millisecond(0)

  return dateWithTime
}

/**
 * Returns the company week start or end date given a date and also having in mind the
 * Company timezone preference
 * @param date date to get the week start or end date for
 * @param target 'start' or 'end' to get the week start or end date for
 * @returns The company week start or end date for the given date
 */
export const getCompanyWeekStartOrEndGivenADate = (
  date: Dayjs,
  target: 'start' | 'end'
) => {
  const { timeZonePreference } = getActiveCompanySettings()

  date = date.tz(timeZonePreference)

  date = target === 'start' ? date.startOf('week') : date.endOf('week')

  //We don't want to set the timezone to again, in this case the timezone is set before
  //calling this function because we need to get the week start or end date in the same timezone
  return setCompanyDayStartAndTimeZone(date, false)
}

/**
 * Get a list of weekdays in the current locale and week start preference
 * e.g. If the week start is on Wednesday, the list will be
 * ['Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', 'Monday', 'Tuesday']
 * @returns an array of weekdays
 */
export const getWeekdayNames = (): DayOfWeek[] => {
  const { activeCompany } = useAppStore.getState()
  const weekStartPreference: number = activeCompany?.preference(
    CompanyPreferences.WeekStart
  )
    ? +activeCompany?.preference(CompanyPreferences.WeekStart)
    : 0

  const weekdays: DayOfWeek[] = Array.from({ length: 7 }, (_, i) => {
    const weekdayName = dayjs()
      .weekday((i + weekStartPreference) % 7)
      .format('dddd') as keyof typeof DayOfWeek

    return DayOfWeek[weekdayName]
  })

  return weekdays
}

export type DayObject = {
  day: string
  date: Dayjs
}
export const getNext7Days = (startDate: string | Dayjs): DayObject[] => {
  const start = dayjs(startDate)
  const days: DayObject[] = []

  for (let i = 0; i < 7; i++) {
    const currentDay = start.add(i, 'day')
    days.push({
      day: currentDay.format('dddd'),
      date: currentDay,
    })
  }

  return days
}

// Helper function to get the day number from the day name
export const getDayNumber = (dayName) => {
  const days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ]
  return days.indexOf(dayName)
}

// Main function to get the array of Dayjs objects
/**
 * Given a start date, it will return an array of dayjs objects in the future
 * for the given day names
 * e.g. We pass the week start of the company and an array of days, it returns
 * an array of dayjs objects for the given day names in the future
 * @param startDate
 * @param dayNames
 * @returnsan an array of dayjs objects for the given day names in the future
 */
export const getMatchingDays = (
  startDate: Dayjs,
  dayNames: (string | DayOfWeek)[]
) => {
  const start = dayjs(startDate)
  const startDayNumber = start.day()

  // Convert day names (strings or enum) to day numbers
  const targetDayNumbers = dayNames.map((day) => getDayNumber(day.toString()))

  // Calculate the dates for the target days
  const resultDates = targetDayNumbers.map((targetDayNumber) => {
    let offset = targetDayNumber - startDayNumber
    if (offset < 0) {
      offset += 7 // Move to the next week if the day has already passed
    }
    return start.add(offset, 'day')
  })

  return resultDates
}

/**
 * Function to check if a Dayjs date is in a list of Dayjs dates,
 * comparing by year, month, and date
 * @param dateList
 * @param dateToCheck
 * @returns boolean
 */
export const isDateInList = (
  dateList: Dayjs[],
  dateToCheck: Dayjs
): boolean => {
  // Format the dateToCheck to 'YYYY-MM-DD'
  const formattedDateToCheck = dateToCheck.format('YYYY-MM-DD')

  // Check if any date in the list matches the formatted date
  return (
    dateList.some(
      (date) => date.format('YYYY-MM-DD') === formattedDateToCheck
    ) || false
  )
}

/**
 * Function to check if a Dayjs date is in a list of Dayjs dates,
 * comparing by year, month, and date and having into account the concept of
 * the company days (e.g. 7am to 7am)
 * @param dateList - Dayjs objects from dateList parameter already with the company start time and timezone set
 * @param dateToCheck
 * @returns boolean
 */
export const isDateInListWithCompanyTime = (
  dateList: Dayjs[],
  dateToCheck: Dayjs,
  timeZone: string
): boolean => {
  return dateList.some((startDate) => {
    const localisedTimestamp = dayjs.tz(startDate, timeZone)
    const localisedCheckTimestamp = dayjs.tz(dateToCheck, timeZone)
    const endTimestamp = localisedTimestamp.add(1, 'day')
    return (
      localisedCheckTimestamp.isSameOrAfter(localisedTimestamp) &&
      localisedCheckTimestamp.isBefore(endTimestamp)
    )
  })
}

export const getRangeDates = (
  date: Dayjs,
  rangeFilter: RangeFilter,
  prevRangeFilter: RangeFilter,
  weekStartPreference?: number
) => {
  let updatedRangeFilter = rangeFilter
  if (updatedRangeFilter === RangeFilter.Custom) {
    updatedRangeFilter = prevRangeFilter
  }

  let startDate = date.startOf(updatedRangeFilter as OpUnitType)
  let endDate = date.endOf(updatedRangeFilter as OpUnitType)

  // Company WeekStart Preference
  if (rangeFilter === RangeFilter.Week && weekStartPreference) {
    if (date.day() === weekStartPreference) {
      startDate = date
      endDate = date.add(6, 'days')
    } else {
      // Adjust the start of the week based on weekStartPreference
      startDate = date.subtract(
        (date.day() - weekStartPreference + 7) % 7,
        'day'
      )

      // Adjust the end of the week based on weekStartPreference
      endDate = startDate.add(6, 'days')
    }
  }

  return {
    startDate,
    endDate,
    updatedRangeFilter,
  }
}

export const getOverlapDays = (
  startOfPeriodA: Dayjs,
  endOfPeriodA: Dayjs,
  startOfPeriodB: Dayjs,
  endOfPeriodB: Dayjs,
  diffUnit: OpUnitType = 'day'
): number => {
  const overlapStart = startOfPeriodA.isAfter(startOfPeriodB)
    ? startOfPeriodA
    : startOfPeriodB
  const overlapEnd = endOfPeriodA.isBefore(endOfPeriodB)
    ? endOfPeriodA
    : endOfPeriodB
  const diff = overlapEnd.diff(overlapStart, diffUnit)

  return diff >= 0 ? diff : 0
}
